import { ErrorHandler, Injectable, InjectionToken, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthStore } from '../../auth.store';
import type { CategoryNodeMap } from '../category/category-node-map';
import { type BrandSlug, tryBrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import { fromMasked } from '../shared/masked-ean';
import { type CompiledSpec, compileSpec } from './segment-to-postgrest-query';
import type { SkuPredicate } from './sku-predicate';

/**
 * The PostgREST max_rows cap (~1000). Any fetch that can exceed this MUST paginate.
 * Brand and category matches routinely contain thousands of EANs (data-access rule).
 */
const PAGE_SIZE = 1000;

/**
 * Injection token for the country-resolved product table name.
 *
 * Provide in the portal module with:
 *   { provide: SEGMENT_PRODUCT_TABLE, useValue: COUNTRY_CONFIG['SE'].productTable }
 *
 * Never hardcode 'medpdb2' in the evaluator itself.
 */
export const SEGMENT_PRODUCT_TABLE = new InjectionToken<string>('SEGMENT_PRODUCT_TABLE');

/**
 * The catalog's own descriptive columns for a product, for callers that render the members
 * rather than only counting them.
 *
 * A column the catalog stores as null becomes the empty string here: the catalog's "no value"
 * and the renderers' "nothing to show" are the same state, and every consumer of these three
 * columns already treats them as plain strings.
 */
export interface CatalogProductMetadata {
  readonly name: string;
  readonly page: string;
  readonly category: string;
}

/**
 * Evaluates a membership formula against the live catalog and returns the current members.
 *
 * A stored segment's formula is Segment.toPredicate(segment) — the pins fold into the spec as
 * all(any(spec, ean-in(include)), not(ean-in(exclude))), one compiled catalog query.
 * A pinned EAN with no catalog row is therefore not a member. A product without a
 * brand is not a valid product (domain ruling 2026-08-20): every query is further
 * constrained to brand_slug not null, so both methods agree on membership.
 *
 * Any query that can exceed PostgREST max_rows is paginated with .range() until
 * the result page is shorter than PAGE_SIZE (data-access rule).
 *
 * Product codes arrive in the catalog's stored form and are decoded here, at the ingress:
 * the wire vocabulary never leaves this ACL, so downstream reads that re-encode an EAN for
 * the wire encode it once. The contract is strict -- a key without the mask marker is a
 * violation, counted and reported once per read, never read as if it were already a real
 * code. The marker cannot tell a stored key from a real code that happens to carry a '5'
 * at index 3; that ambiguity is inherent to the stored format and is accepted.
 */
@Injectable()
export class SegmentEvaluatorService {
  private readonly authStore = inject(AuthStore);
  private readonly productTable = inject(SEGMENT_PRODUCT_TABLE);
  private readonly errorHandler = inject(ErrorHandler);

  private client(): Promise<SupabaseClient> {
    return this.authStore.getSupabaseClient();
  }

  /**
   * Evaluate the formula's membership against the current catalog state.
   *
   * nodeMap must be pre-built from portal_categories (caller's responsibility —
   * the evaluator does not load portal_categories itself).
   */
  async evaluate(predicate: SkuPredicate, nodeMap: CategoryNodeMap): Promise<ReadonlySet<EAN>> {
    const compiled = compileSpec(predicate, nodeMap);
    return this.fetchSpecEANs(compiled);
  }

  /**
   * Evaluate the formula's membership, returning the brand_slug for each member EAN.
   * Used by the brand-observations fallback path in MeasurementEvaluatorService.
   * nodeMap must be pre-built from portal_categories (same requirement as evaluate()).
   */
  async evaluateWithBrand(
    predicate: SkuPredicate,
    nodeMap: CategoryNodeMap
  ): Promise<ReadonlyMap<EAN, BrandSlug>> {
    const compiled = compileSpec(predicate, nodeMap);
    return this.fetchSpecEANsWithBrand(compiled);
  }

  /**
   * Evaluate the formula's membership, returning the catalog's descriptive columns for each
   * member EAN. One query resolves membership and the metadata together, so a caller that
   * renders its members never issues a second round trip to describe them.
   * nodeMap must be pre-built from portal_categories (same requirement as evaluate()).
   */
  async evaluateWithMetadata(
    predicate: SkuPredicate,
    nodeMap: CategoryNodeMap
  ): Promise<ReadonlyMap<EAN, CatalogProductMetadata>> {
    const compiled = compileSpec(predicate, nodeMap);
    return this.fetchSpecEANsWithMetadata(compiled);
  }

  /**
   * Whether the formula has any member at all, asked as one capped row rather than a scan.
   *
   * A caller that only needs the bit — an option gate, a "does this intersect" test — would
   * otherwise page a whole intersection to look at its size. One `limit(1)` read answers the
   * same question and stops at the first row.
   *
   * The one row is decoded as strictly as a full read decodes its rows, so a member whose
   * stored key is not a masked EAN is reported and does not count. That matches what
   * `evaluate()` would say about that same row, and it is the only way the two can disagree:
   * a scan that reaches a later, well-formed member sees a member this read never fetched.
   *
   * That divergence is accepted, but note what it is: the read is unordered, so WHICH row the
   * `limit(1)` returns is the planner's choice and can change between two identical calls. The
   * disagreement is therefore plan-dependent and flappy, not a stable "this formula has no
   * members" — a caller that must not flip between runs wants `evaluate()`.
   */
  async hasMembers(predicate: SkuPredicate, nodeMap: CategoryNodeMap): Promise<boolean> {
    const compiled = compileSpec(predicate, nodeMap);
    if (compiled.kind === 'short-circuit') return false;

    const supabase = await this.client();
    const base = supabase.from(this.productTable).select('EAN');
    const filtered = compiled.kind === 'filter' ? base.or(compiled.orString) : base;
    const { data, error } = await filtered.not('brand_slug', 'is', null).limit(1);

    if (error) throw error;
    const rows = data as { EAN: string }[];
    if (rows.length === 0) return false;

    const ean = fromMasked(String(rows[0].EAN));
    this.reportUnstoredKeys('hasMembers', ean ? 0 : 1);
    return ean !== null;
  }

  private async fetchSpecEANs(compiled: CompiledSpec): Promise<ReadonlySet<EAN>> {
    const rows = await this.fetchRows<{ EAN: string }, true>(
      compiled,
      'EAN',
      'evaluate',
      () => true
    );
    return new Set(rows.keys());
  }

  private async fetchSpecEANsWithBrand(compiled: CompiledSpec): Promise<Map<EAN, BrandSlug>> {
    return this.fetchRows<{ EAN: string; brand_slug: string }, BrandSlug>(
      compiled,
      'EAN, brand_slug',
      'evaluateWithBrand',
      (row) => tryBrandSlug(row.brand_slug ?? '')
    );
  }

  private async fetchSpecEANsWithMetadata(
    compiled: CompiledSpec
  ): Promise<Map<EAN, CatalogProductMetadata>> {
    return this.fetchRows<
      { EAN: string; name: string | null; page: string | null; category: string | null },
      CatalogProductMetadata
    >(compiled, 'EAN, name, page, category', 'evaluateWithMetadata', (row) => ({
      name: row.name ?? '',
      page: row.page ?? '',
      category: row.category ?? '',
    }));
  }

  /**
   * Every member of the compiled query, keyed by decoded EAN, projected column by column.
   *
   * The pages are ordered by EAN because `.range()` over an unordered relation is not a
   * partition: Postgres may return the same row on two pages and no row on any, so an
   * unordered scan can silently drop members. A total order over a unique column makes the
   * page boundaries stable for the life of the scan.
   *
   * `project` returning null drops the row without counting it as a key violation — that is
   * a column the caller could not use, not a product code the catalog stored wrong.
   */
  private async fetchRows<Row extends { EAN: string }, T>(
    compiled: CompiledSpec,
    columns: string,
    method: string,
    project: (row: Row) => T | null
  ): Promise<Map<EAN, T>> {
    const rows = new Map<EAN, T>();
    if (compiled.kind === 'short-circuit') return rows;

    const supabase = await this.client();
    let skipped = 0;
    let offset = 0;

    while (true) {
      const base = supabase.from(this.productTable).select(columns);
      const filtered = compiled.kind === 'filter' ? base.or(compiled.orString) : base;
      const { data, error } = await filtered
        .not('brand_slug', 'is', null)
        .order('EAN', { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) throw error;
      // supabase-js infers a row type from a LITERAL column list only; a parameterized one
      // yields GenericStringError, so the caller's declared Row stands in for the inference.
      const page = data as unknown as Row[];
      for (const row of page) {
        const ean = fromMasked(String(row.EAN));
        if (!ean) {
          skipped++;
          continue;
        }
        const value = project(row);
        if (value !== null) rows.set(ean, value);
      }

      if (page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    this.reportUnstoredKeys(method, skipped);
    return rows;
  }

  /**
   * One report per read, never per row: a systemic key-format break in the catalog
   * would otherwise be one Sentry event per product of every evaluated segment.
   */
  private reportUnstoredKeys(method: string, count: number): void {
    if (count === 0) return;
    this.errorHandler.handleError(
      new Error(
        `[SegmentEvaluator] ${method}: ${count} ${this.productTable} row(s) carry a product key that is not a masked EAN; skipped`
      )
    );
  }
}
